// Sagittal range-of-motion (ESTIMATED) for the lateral view.
//
// Single-camera 2D pose cannot deliver lab-grade joint angles, so these values
// are clearly labeled "estimated", carry a confidence, and are intended to give
// a feel for ROM — not for diagnostic angle measurement. Only sagittal
// (lateral-view) angles are produced; coronal angles (valgus/varus) stay
// categorical elsewhere because 2D cannot estimate them reliably.

import { Joints, isotropicFrame, type PoseFrame, type Side } from "../pose/landmarks";
import type { EventResult, GaitCycle } from "./events";
import { angleAt, signedAngleDeg, percentile, vec } from "./geometry";
import { savitzkyGolay } from "../pose/smoothing";
import { gateSeries } from "./robustness";
import { phaseOfFraction, PHASES, type Phase } from "./phases";
import { mean } from "./signal";

export interface RomRange {
  min: number;
  max: number;
  excursion: number;
}

export interface JointRom {
  L: RomRange;
  R: RomRange;
}

export type JointKey = "hip" | "knee" | "ankle";

export interface SagittalRom {
  hip: JointRom;
  knee: JointRom;
  ankle: JointRom;
  /** Mean angle per phase per side, per joint (the OPED page-3 equivalent). */
  perPhase: Record<JointKey, Record<Phase, { L: number | null; R: number | null }>>;
  /** Full normalized cycle-averaged waveform per side, per joint (0..100%). */
  curves: Record<JointKey, { L: number[]; R: number[] }>;
  confidence: "high" | "medium" | "low";
  estimated: true;
}

/** Per-frame sagittal angle series for one joint+side. forward = walking dir. */
function jointSeries(
  frames: PoseFrame[],
  side: Side,
  joint: JointKey,
  direction: number,
): number[] {
  const raw = frames.map((f) => {
    const j = new Joints(f);
    if (joint === "knee") {
      // 0° = straight, + = flexed.
      return 180 - angleAt(j.hip(side), j.knee(side), j.ankle(side));
    }
    if (joint === "hip") {
      // Thigh relative to trunk axis; + = flexion (thigh forward), − = extension.
      const trunkDown = vec(j.shoulderCenter(), j.pelvisCenter());
      const thigh = vec(j.hip(side), j.knee(side));
      return signedAngleDeg(trunkDown, thigh) * direction;
    }
    // ankle: foot relative to shank; + = dorsiflexion. Offset so neutral ≈ 0.
    const shank = vec(j.knee(side), j.ankle(side));
    const foot = vec(j.ankle(side), j.footIndex(side));
    return (signedAngleDeg(shank, foot) * direction) - 90;
  });
  // Visibility gate by the landmarks each joint angle actually depends on, so an
  // occluded far-leg sample doesn't distort the estimated ROM percentiles.
  const vis = frames.map((f) => {
    const j = new Joints(f);
    if (joint === "knee") {
      return Math.min(j.hip(side).visibility, j.knee(side).visibility, j.ankle(side).visibility);
    }
    if (joint === "hip") {
      return Math.min(
        j.shoulderCenter().visibility,
        j.hip(side).visibility,
        j.knee(side).visibility,
      );
    }
    return Math.min(j.knee(side).visibility, j.ankle(side).visibility, j.footIndex(side).visibility);
  });
  return savitzkyGolay(gateSeries(raw, vis), 7);
}

function rangeOf(series: number[]): RomRange {
  const min = percentile(series, 5);
  const max = percentile(series, 95);
  return { min, max, excursion: Math.max(0, max - min) };
}

export function computeSagittalRom(
  frames: PoseFrame[],
  events: EventResult,
  aspect = 1,
): SagittalRom {
  const dir = events.direction;
  const joints: JointKey[] = ["hip", "knee", "ankle"];

  // Isotropic image space so sagittal angle estimates aren't aspect-distorted.
  // aspect = 1 reproduces the previous (raw-coordinate) behaviour exactly.
  const fr = frames.map((f) => isotropicFrame(f, aspect));

  const series: Record<JointKey, Record<Side, number[]>> = {
    hip: { L: jointSeries(fr, "L", "hip", dir), R: jointSeries(fr, "R", "hip", dir) },
    knee: { L: jointSeries(fr, "L", "knee", dir), R: jointSeries(fr, "R", "knee", dir) },
    ankle: { L: jointSeries(fr, "L", "ankle", dir), R: jointSeries(fr, "R", "ankle", dir) },
  };

  const romFor = (joint: JointKey): JointRom => ({
    L: rangeOf(series[joint].L),
    R: rangeOf(series[joint].R),
  });

  // Per-phase means + cycle-averaged curves.
  const perPhase = emptyPerPhase();
  const curves: SagittalRom["curves"] = {
    hip: { L: [], R: [] },
    knee: { L: [], R: [] },
    ankle: { L: [], R: [] },
  };

  for (const side of ["L", "R"] as Side[]) {
    const cycles = events.cycles.filter((c) => c.side === side);
    for (const joint of joints) {
      const phaseBuckets: Record<Phase, number[]> = emptyBuckets();
      const grid = resampleCycles(frames, cycles, series[joint][side]);
      curves[joint][side] = grid;
      // Assign frames to phases for the per-phase table.
      for (const c of cycles) {
        const toeOffFrac = cycleToeOffFrac(c);
        for (let i = 0; i < frames.length; i++) {
          const t = frames[i].tMs;
          if (t < c.startMs || t > c.endMs) continue;
          const frac = (t - c.startMs) / Math.max(1, c.durationMs);
          phaseBuckets[phaseOfFraction(frac, toeOffFrac)].push(series[joint][side][i]);
        }
      }
      for (const p of PHASES) {
        perPhase[joint][p][side] = phaseBuckets[p].length ? mean(phaseBuckets[p]) : null;
      }
    }
  }

  const confidence = romConfidence(frames, events.cycles);

  return {
    hip: romFor("hip"),
    knee: romFor("knee"),
    ankle: romFor("ankle"),
    perPhase,
    curves,
    confidence,
    estimated: true,
  };
}

/** Resample each cycle's series onto a 0..100% grid (101 pts) and average. */
function resampleCycles(
  frames: PoseFrame[],
  cycles: GaitCycle[],
  series: number[],
): number[] {
  const N = 101;
  const acc = new Array(N).fill(0);
  const cnt = new Array(N).fill(0);
  for (const c of cycles) {
    const idxs: number[] = [];
    for (let i = 0; i < frames.length; i++) {
      if (frames[i].tMs >= c.startMs && frames[i].tMs <= c.endMs) idxs.push(i);
    }
    if (idxs.length < 3) continue;
    for (let g = 0; g < N; g++) {
      const frac = g / (N - 1);
      const t = c.startMs + frac * c.durationMs;
      // nearest sample
      let best = idxs[0];
      for (const i of idxs) if (Math.abs(frames[i].tMs - t) < Math.abs(frames[best].tMs - t)) best = i;
      acc[g] += series[best];
      cnt[g] += 1;
    }
  }
  return acc.map((v, g) => (cnt[g] ? v / cnt[g] : 0));
}

function cycleToeOffFrac(c: GaitCycle): number {
  if (c.toeOffMs === null || c.durationMs <= 0) return 0.62;
  return Math.min(0.9, Math.max(0.4, (c.toeOffMs - c.startMs) / c.durationMs));
}

function romConfidence(
  frames: PoseFrame[],
  cycles: GaitCycle[],
): "high" | "medium" | "low" {
  const vis = mean(frames.map((f) => mean(f.landmarks.map((l) => l.visibility))));
  if (vis >= 0.7 && cycles.length >= 3) return "high";
  if (vis >= 0.55 && cycles.length >= 2) return "medium";
  return "low";
}

function emptyBuckets(): Record<Phase, number[]> {
  const o = {} as Record<Phase, number[]>;
  for (const p of PHASES) o[p] = [];
  return o;
}

function emptyPerPhase(): SagittalRom["perPhase"] {
  const mk = () => {
    const o = {} as Record<Phase, { L: number | null; R: number | null }>;
    for (const p of PHASES) o[p] = { L: null, R: null };
    return o;
  };
  return { hip: mk(), knee: mk(), ankle: mk() };
}
